import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import {Form, Formik} from 'formik';
import {PLCheckbox, PLDatePicker, PLTextField} from '../PLFields';
import CloseIcon from '@mui/icons-material/Close';

interface ValueData {
    id: string;
    label: string;
}

//Simplified interface for no-query input forms
export interface StrictInputFields {
    key: string;
    caption: string;
    disabled?: boolean;
    disableField?: string;
    disableValue?: any;
    initVal?;
    type?: string;
    validation?: ((value) => boolean) | ((value, values) => boolean);
    validationError?: string;
    valueList?: ValueData[];
}

//Extra fields to allow query-based input form definitions
export interface InputFormFields extends StrictInputFields {
    initVal?;
    addMissingInitVal?: boolean;
    valueQuery?;
    simpleValueQuery?: boolean;
    valueQueryArgs?;
    valueQueryIdIndex?: string;
    valueQueryNameIndex?: string;
    valueQuerySecondaryNameIndex?: string;
}

export interface ButtonSelectOption {
    caption: string;
    action: () => any;
}

///<summary>Simple OK/Cancel form</summary>
///<param name="modalContext">React Modal Context</param>
///<param name="title">Title to show in the dialog</param>
///<param name="mainCaption">Caption to show in the dialog</param>
///<param name="okCaption">Caption to show on the OK button</param>
///<param name="okHandler">Function to call if OK is clicked</param>
///<param name="cancelCaption">Caption to show on the Cancel button</param>
///<param name="cancelHandler">Function to call if Cancel is clicked</param>
export const okCancelForm = (
    modalContext,
    title,
    mainCaption,
    okCaption,
    okHandler,
    cancelCaption?,
    cancelHandler?
) => {
    if (!cancelCaption) {
        cancelCaption = 'Cancel';
    }
    if (!cancelHandler) {
        cancelHandler = modalContext.closeModal;
    }

    modalContext.showModal(({show}) => (
        <Dialog open={show}>
            <DialogTitle sx={{whiteSpace: 'break-spaces'}}>{title}</DialogTitle>
            <IconButton
                onClick={modalContext.closeModal}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseIcon />
            </IconButton>
            <Typography
                variant="body1"
                style={{
                    textAlign: 'center',
                    whiteSpace: 'break-spaces',
                    padding: '0px 16px',
                }}>
                {mainCaption}
            </Typography>
            <DialogActions>
                <Button onClick={okHandler}>{okCaption}</Button>
                <Button color="secondary" onClick={cancelHandler}>
                    {cancelCaption}
                </Button>
            </DialogActions>
        </Dialog>
    ));
};

///<summary>Simple OK-only form</summary>
///<param name="modalContext">React Modal Context</param>
///<param name="title">"Title" caption to show in the dialog</param>
///<param name="subtitle">Explanatory caption to expand on the title</param>
///<param name="clickHandler">Function to call when OK is clicked</param>
///<param name="defaultOk">Do we focus the OK button immediately for easy dismissal?</param>
export const okOnlyForm = (
    modalContext,
    title,
    subtitle,
    clickHandler,
    defaultOk?
) => {
    modalContext.showModal(({show}) => (
        <Dialog open={show}>
            <DialogContent>
                <DialogTitle sx={{whiteSpace: 'break-spaces'}}>
                    {title}
                </DialogTitle>
                <IconButton
                    onClick={clickHandler}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}>
                    <CloseIcon />
                </IconButton>
                <Typography
                    variant="body1"
                    style={{textAlign: 'center', whiteSpace: 'break-spaces'}}>
                    {subtitle}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={clickHandler} autoFocus={defaultOk}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    ));
};

///<summary>A somewhat more flexible OK/Cancel form, that allows JSX content and custom button caption</summary>
///<param name="modalContext">React Modal Context</param>
///<param name="title">"Title" caption to show in the dialog</param>
///<param name="content">JSX content to show in the dialog</param>
///<param name="okCaption">Caption to show on the OK button</param>
///<param name="okHandler">Function to call when OK is clicked</param>
///<param name="cancelCaption">Caption to show on the Cancel button (or blank to hide it)</param>
///<param name="cancelHandler">Function to call when Cancel is clicked</param>
export const flexibleOkCancelForm = (
    modalContext,
    title,
    content,
    okCaption,
    okHandler,
    cancelCaption,
    cancelHandler
) => {
    modalContext.showModal(({show}) => (
        <Dialog open={show} maxWidth="md" fullWidth>
            <DialogContent>
                <DialogTitle sx={{whiteSpace: 'break-spaces'}}>
                    {title}
                </DialogTitle>
                <IconButton
                    onClick={cancelHandler ? cancelHandler : okHandler}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}>
                    <CloseIcon />
                </IconButton>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={okHandler}>{okCaption}</Button>
                {cancelCaption ? (
                    <Button onClick={cancelHandler}>{cancelCaption}</Button>
                ) : (
                    <></>
                )}
            </DialogActions>
        </Dialog>
    ));
};

///<summary>Simple form supporting arbitrary number of text input fields with simple validation.
///  Does not optimize to use horizontal space for many fields, nor does it support custom validation.</summary >
///<param name="modalContext">React Modal Context</param>
///<param name="title">"Title" caption to show in the dialog</param>
///<param name="fields">Array of data for the fields to display.
///  Respected fields are listed in the InputFormFields interface</param>
///<param name="submitHandler">Function to call when OK is clicked</param>
///<param name="okCaption">Caption to show on the OK button</param>
///<param name="cancelHandler">Function to call when Cancel is clicked</param>
export const inputFieldsForm = async (
    modalContext,
    title,
    fields: InputFormFields[],
    submitHandler,
    okCaption,
    cancelHandler?
) => {
    //Wrapper to prevent the Enter key being used on an entry field triggering
    //Formik's auto-accept functionality
    const skipEnterAccept = (event) => {
        event.key === 'Enter' && event.preventDefault();
    };

    let initVals = {};
    for (let i = 0; i < fields.length; i++) {
        initVals[fields[i].key] = fields[i].initVal;
        //No way to pre-resolve queries and pass their values in during init,
        // so we need to instead resolve the queries
        // and parcel out their data into the lists here
        if (fields[i].valueQuery) {
            await fields[i]
                .valueQuery(fields[i].valueQueryArgs)
                .unwrap()
                .then((result) => {
                    fields[i].valueList = result.map((value) => {
                        if (fields[i].simpleValueQuery) {
                            // Support both primitive lists and object-based dropdown structs.
                            if (
                                value !== null &&
                                typeof value === 'object' &&
                                !Array.isArray(value)
                            ) {
                                const simpleObj = value as any;
                                const id =
                                    simpleObj.value ??
                                    simpleObj.id ??
                                    simpleObj.code ??
                                    simpleObj.key ??
                                    simpleObj.name ??
                                    '';
                                const label =
                                    simpleObj.display ??
                                    simpleObj.label ??
                                    simpleObj.description ??
                                    simpleObj.name ??
                                    id;
                                return {id, label};
                            }

                            return {id: value, label: value};
                        }
                        return {
                            id: value[fields[i].valueQueryIdIndex],
                            label:
                                value[fields[i].valueQueryNameIndex].trim()
                                    .length !== 0
                                    ? value[fields[i].valueQueryNameIndex]
                                    : value[
                                          fields[i].valueQuerySecondaryNameIndex
                                      ],
                        };
                    });

                    if (
                        fields[i].initVal &&
                        fields[i].valueList.indexOf(fields[i].initVal) === -1 &&
                        fields[i].addMissingInitVal
                    ) {
                        fields[i].valueList.unshift({
                            id: fields[i].initVal,
                            label: fields[i].initVal,
                        });
                    }
                });
        }
    }

    if (cancelHandler === undefined) {
        cancelHandler = () => modalContext.closeModal();
    }

    modalContext.showModal(({show}) => (
        <Dialog open={show} fullWidth>
            <Formik
                initialValues={initVals}
                validate={(values) => {
                    let errors = {};
                    for (let i = 0; i < fields.length; i++) {
                        const validationFunc = fields[i].validation;
                        if (
                            validationFunc &&
                            !validationFunc(values[fields[i].key], values)
                        ) {
                            errors[fields[i].key] = fields[i].validationError;
                        }
                    }
                    return errors;
                }}
                validateOnMount
                onSubmit={submitHandler}>
                {({
                    values,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    errors,
                }) => (
                    <Form onSubmit={handleSubmit}>
                        <DialogContent>
                            <DialogTitle>{title}</DialogTitle>
                            <IconButton
                                onClick={cancelHandler}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: (theme) => theme.palette.grey[500],
                                }}>
                                <CloseIcon />
                            </IconButton>
                            {fields.map((field, index) => {
                                let fieldDisabled = false;
                                if (field.disabled) {
                                    fieldDisabled = true;
                                } else if (
                                    field.disableField &&
                                    field.disableValue ===
                                        values[field.disableField]
                                ) {
                                    fieldDisabled = true;
                                }
                                if (field.type === 'select') {
                                    const options = (field.valueList || []).map(
                                        (value) => {
                                            const rawLabel = value.label;
                                            return {
                                                label:
                                                    typeof rawLabel === 'string'
                                                        ? rawLabel
                                                        : String(rawLabel ?? ''),
                                                id:
                                                    value.id !== ''
                                                        ? value.id
                                                        : '*',
                                            };
                                        }
                                    );

                                    const selectedOption =
                                        options.find(
                                            (option) =>
                                                option.id === values[field.key]
                                        ) || null;

                                    return (
                                        <Autocomplete
                                            id={field.key}
                                            options={options}
                                            onChange={(e, value) =>
                                                setFieldValue(
                                                    field.key,
                                                    typeof value === 'string'
                                                        ? value
                                                        : value?.id || ''
                                                )
                                            }
                                            value={selectedOption}
                                            disabled={fieldDisabled}
                                            selectOnFocus
                                            fullWidth
                                            onBlur={handleBlur}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string'
                                                    ? option
                                                    : String(
                                                          option?.label ??
                                                              option?.id ??
                                                              ''
                                                      )
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    key={field.key}
                                                    name={field.key}
                                                    label={field.caption}
                                                    onKeyDown={skipEnterAccept}
                                                    error={Boolean(
                                                        errors[field.key]
                                                    )}
                                                    helperText={
                                                        errors[field.key]
                                                            ? errors[field.key]
                                                            : ''
                                                    }
                                                    style={
                                                        index <
                                                        fields.length - 1
                                                            ? {
                                                                  padding:
                                                                      '8px 0px 8px 0px',
                                                              }
                                                            : {
                                                                  paddingTop:
                                                                      '8px',
                                                              }
                                                    }
                                                    autoFocus={index === 0}
                                                    {...params}
                                                />
                                            )}
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) =>
                                                option.id ===
                                                (typeof value === 'string'
                                                    ? value
                                                    : value?.id)}
                                        />
                                    );
                                } else if (field.type === 'checkbox') {
                                    return (
                                        <PLCheckbox
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            checked={values[field.key]}
                                            disabled={fieldDisabled}
                                            onChange={(event) =>
                                                setFieldValue(
                                                    field.key,
                                                    !!event.target.checked
                                                )
                                            }
                                            onBlur={handleBlur}
                                            autoFocus={index === 0}
                                        />
                                    );
                                } else if (field.type === 'largeText') {
                                    return (
                                        <PLTextField
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    setFieldValue(
                                                        field.key,
                                                        values[field.key] + '\n'
                                                    );
                                                }
                                            }}
                                            style={
                                                index < fields.length - 1
                                                    ? {
                                                          padding:
                                                              '8px 0px 8px 0px',
                                                      }
                                                    : {paddingTop: '8px'}
                                            }
                                            fullWidth
                                            multiline
                                            minRows={5}
                                            maxRows={5}
                                            autoFocus={index === 0}
                                        />
                                    );
                                } else if (field.type === 'date') {
                                    return (
                                        <PLDatePicker
                                            id={field.key}
                                            key={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={(value) =>
                                                setFieldValue(field.key, value)
                                            }
                                            onKeyDown={skipEnterAccept}
                                        />
                                    );
                                } else {
                                    return (
                                        <TextField
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={skipEnterAccept}
                                            style={
                                                index < fields.length - 1
                                                    ? {
                                                          padding:
                                                              '8px 0px 8px 0px',
                                                      }
                                                    : {paddingTop: '8px'}
                                            }
                                            fullWidth
                                            autoFocus={index === 0}
                                        />
                                    );
                                }
                            })}
                        </DialogContent>
                        <DialogActions>
                            <Button type="submit">{okCaption}</Button>
                            <Button color="secondary" onClick={cancelHandler}>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    ));
};

///<summary>Simpler form supporting arbitrary number of text input fields with simple validation.
///  Does not optimize to use horizontal space for many fields, nor does it support custom validation.
///  Also does not support setting up selection fields using queries directly.</summary >
///<param name="modalContext">React Modal Context</param>
///<param name="title">"Title" caption to show in the dialog</param>
///<param name="fields">Array of data for the fields to display.
///  Respected fields are listed in the StrictInputFields interface</param>
///<param name="submitHandler">Function to call when OK is clicked</param>
///<param name="okCaption">Caption to show on the OK button</param>
export const strictInputForm = async (
    modalContext,
    title,
    fields: StrictInputFields[],
    submitHandler,
    okCaption
) => {
    //Wrapper to prevent the Enter key being used on an entry field triggering
    //Formik's auto-accept functionality
    const skipEnterAccept = (event) => {
        event.key === 'Enter' && event.preventDefault();
    };

    let initVals = {};
    for (let i = 0; i < fields.length; i++) {
        initVals[fields[i].key] = fields[i].initVal;
    }

    modalContext.showModal(({show}) => (
        <Dialog open={show} fullWidth>
            <Formik
                initialValues={initVals}
                validate={(values) => {
                    let errors = {};
                    for (let i = 0; i < fields.length; i++) {
                        const validationFunc = fields[i].validation;
                        if (
                            validationFunc &&
                            !validationFunc(values[fields[i].key], values)
                        ) {
                            errors[fields[i].key] = fields[i].validationError;
                        }
                    }
                    return errors;
                }}
                validateOnMount
                onSubmit={submitHandler}>
                {({
                    values,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    errors,
                }) => (
                    <Form onSubmit={handleSubmit}>
                        <DialogContent>
                            <DialogTitle>{title}</DialogTitle>
                            <IconButton
                                onClick={modalContext.closeModal}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: (theme) => theme.palette.grey[500],
                                }}>
                                <CloseIcon />
                            </IconButton>
                            {fields.map((field, index) => {
                                let fieldDisabled = false;
                                if (field.disabled) {
                                    fieldDisabled = true;
                                } else if (
                                    field.disableField &&
                                    field.disableValue ===
                                        values[field.disableField]
                                ) {
                                    fieldDisabled = true;
                                }
                                if (field.type === 'select') {
                                    const options = (field.valueList || []).map(
                                        (value) => {
                                            const rawLabel = value.label;
                                            return {
                                                label:
                                                    typeof rawLabel === 'string'
                                                        ? rawLabel
                                                        : String(rawLabel ?? ''),
                                                id:
                                                    value.id !== ''
                                                        ? value.id
                                                        : '*',
                                            };
                                        }
                                    );

                                    const selectedOption =
                                        typeof values[field.key] === 'string'
                                            ? options.find(
                                                  (option) =>
                                                      option.id ===
                                                      values[field.key]
                                              ) || null
                                            : values[field.key] || null;

                                    return (
                                        <Autocomplete
                                            id={field.key}
                                            options={options}
                                            onChange={(e, value) =>
                                                setFieldValue(
                                                    field.key,
                                                    value || null
                                                )
                                            }
                                            value={selectedOption}
                                            disabled={fieldDisabled}
                                            selectOnFocus
                                            fullWidth
                                            onBlur={handleBlur}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string'
                                                    ? option
                                                    : String(
                                                          option?.label ??
                                                              option?.id ??
                                                              ''
                                                      )
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    key={field.key}
                                                    name={field.key}
                                                    label={field.caption}
                                                    onKeyDown={skipEnterAccept}
                                                    error={Boolean(
                                                        errors[field.key]
                                                    )}
                                                    helperText={
                                                        errors[field.key]
                                                            ? errors[field.key]
                                                            : ''
                                                    }
                                                    style={
                                                        index <
                                                        fields.length - 1
                                                            ? {
                                                                  padding:
                                                                      '8px 0px 8px 0px',
                                                              }
                                                            : {
                                                                  paddingTop:
                                                                      '8px',
                                                              }
                                                    }
                                                    autoFocus={index === 0}
                                                    {...params}
                                                />
                                            )}
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) =>
                                                option.id ===
                                                (typeof value === 'string'
                                                    ? value
                                                    : value?.id)}
                                        />
                                    );
                                } else if (field.type === 'checkbox') {
                                    return (
                                        <PLCheckbox
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            checked={values[field.key]}
                                            disabled={fieldDisabled}
                                            onChange={(event) =>
                                                setFieldValue(
                                                    field.key,
                                                    !!event.target.checked
                                                )
                                            }
                                            onBlur={handleBlur}
                                            autoFocus={index === 0}
                                        />
                                    );
                                } else if (field.type === 'largeText') {
                                    return (
                                        <PLTextField
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    setFieldValue(
                                                        field.key,
                                                        values[field.key] + '\n'
                                                    );
                                                }
                                            }}
                                            style={
                                                index < fields.length - 1
                                                    ? {
                                                          padding:
                                                              '8px 0px 8px 0px',
                                                      }
                                                    : {paddingTop: '8px'}
                                            }
                                            fullWidth
                                            multiline
                                            minRows={5}
                                            maxRows={5}
                                            autoFocus={index === 0}
                                        />
                                    );
                                } else if (field.type === 'date') {
                                    return (
                                        <PLDatePicker
                                            id={field.key}
                                            key={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={(value) =>
                                                setFieldValue(field.key, value)
                                            }
                                            onKeyDown={skipEnterAccept}
                                        />
                                    );
                                } else {
                                    return (
                                        <TextField
                                            id={field.key}
                                            key={field.key}
                                            name={field.key}
                                            label={field.caption}
                                            value={values[field.key]}
                                            disabled={fieldDisabled}
                                            error={Boolean(errors[field.key])}
                                            helperText={
                                                errors[field.key]
                                                    ? errors[field.key]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={skipEnterAccept}
                                            style={
                                                index < fields.length - 1
                                                    ? {
                                                          padding:
                                                              '8px 0px 8px 0px',
                                                      }
                                                    : {paddingTop: '8px'}
                                            }
                                            fullWidth
                                            autoFocus={index === 0}
                                        />
                                    );
                                }
                            })}
                        </DialogContent>
                        <DialogActions>
                            <Button type="submit">{okCaption}</Button>
                            <Button
                                color="secondary"
                                onClick={() => {
                                    modalContext.closeModal();
                                }}>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    ));
};
///<summary>Form that presents options as a series of buttons to pick from</summary>
///<param name="modalContext">React Modal Context</param>
///<param name="title">"Title" caption to show in the dialog</param>
///<param name="caption">Caption to show for explaining buttons</param>
///<param name="buttons">Array of data for the buttons to display.
///  Respected fields are listed in the ButtonSelectOption interface</param>
export const buttonSelectForm = async (
    modalContext,
    title,
    caption,
    buttons: ButtonSelectOption[]
) => {
    modalContext.showModal(({show}) => (
        <Dialog open={show} fullWidth>
            <DialogContent>
                <DialogTitle>{title}</DialogTitle>
                <IconButton
                    onClick={modalContext.closeModal}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}>
                    <CloseIcon />
                </IconButton>
                <Typography
                    variant="body1"
                    style={{textAlign: 'center', whiteSpace: 'break-spaces'}}>
                    {caption}
                </Typography>
            </DialogContent>
            <DialogActions>
                {buttons.map((button) => {
                    return (
                        <Button
                            color="primary"
                            onClick={() => {
                                modalContext.closeModal();
                                button.action();
                            }}>
                            {button.caption}
                        </Button>
                    );
                })}
                <Button
                    color="secondary"
                    onClick={() => {
                        modalContext.closeModal();
                    }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    ));
};
